import {
  Box,
  Button,
  Code,
  Flex,
  Heading,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

interface QueryExecute {
  encodedQuery: string;
  shouldExecute: boolean;
  onClose: () => void;
}
const Base_apiUrl=process.env.REACT_APP_API_Base_Url;
const QueryExecutor: React.FC<QueryExecute> = ({
  encodedQuery,
  shouldExecute,
  onClose,
}) => {
  const [executionResult, setExecutionResult] = useState<any>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const bgColor = useColorModeValue("white", "gray.800");

  useEffect(() => {
    if (shouldExecute && encodedQuery) {
      executeQuery();
    }
  }, [shouldExecute, encodedQuery]);

  const executeQuery = async () => {
    try {
      setIsExecuting(true);
      const response = await fetch(
        `${Base_apiUrl}Chat/ExecuteGeneratedQuery?GeneratedQuery=${encodedQuery}`
      );
      if (!response.ok) throw new Error("Failed to execute query");

      const result = await response.json();

      // Try to detect where the actual data is
      const message = result.message ?? result.data ?? result;

      setExecutionResult(message);
    } catch (err: any) {
      setExecutionResult({ error: err.message || "Unknown error" });
    } finally {
      setIsExecuting(false);
    }
  };

  const downloadJSON = () => {
    const blob = new Blob(
      [JSON.stringify(executionResult, null, 2)],
      { type: "application/json" }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `query-result-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadCSV = () => {
    try {
      let jsonData =
        typeof executionResult === "string"
          ? JSON.parse(executionResult)
          : executionResult;

      if (!Array.isArray(jsonData)) {
        if (typeof jsonData === "object" && jsonData !== null) {
          jsonData = [jsonData]; // wrap object
        } else {
          throw new Error("Data not suitable for CSV download");
        }
      }

      const headers = Object.keys(jsonData[0]);
      const csvRows = [
        headers.join(","),
        ...jsonData.map((row: any) =>
          headers
            .map((field) => {
              const val = row[field] ?? "";
              const safe = String(val).replace(/"/g, '""');
              return `"${safe}"`;
            })
            .join(",")
        ),
      ];

      const blob = new Blob([csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `query-result-${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      alert("Failed to download CSV: " + error.message);
    }
  };

  return (
    <Flex direction="column" w="100%" maxW="1000px" gap={4}>
      {/* Query Display */}
      <Box>
        <Flex justify="space-between" align="center" mb={2}>
          <Heading size="sm">Query</Heading>
          <Button
            colorScheme="green"
            size="sm"
            onClick={executeQuery}
            isLoading={isExecuting}
            loadingText="Executing..."
          >
            Execute Query
          </Button>
        </Flex>
        <Code
          display="block"
          whiteSpace="pre"
          p={2}
          borderRadius="md"
          fontSize="sm"
          width="100%"
          overflowX="auto"
        >
          {encodedQuery}
        </Code>
      </Box>

      {/* Execution Result */}
      <Box>
        <Heading size="sm" mb={2}>
          Execution Result
        </Heading>
        <Flex mb={2}>
          <Button
            size="xs"
            colorScheme="blue"
            mr={2}
            onClick={downloadJSON}
            isDisabled={!executionResult}
          >
            Download JSON
          </Button>
          <Button
            size="xs"
            colorScheme="green"
            onClick={downloadCSV}
            isDisabled={!executionResult}
          >
            Download CSV
          </Button>
        </Flex>
        <Code
          display="block"
          whiteSpace="pre"
          p={2}
          borderRadius="md"
          fontSize="sm"
          width="100%"
          overflowY="auto"
          maxH="300px"
        >
          {isExecuting ? <Spinner size="sm" mr={2} /> : null}
          {typeof executionResult === "string"
            ? executionResult
            : JSON.stringify(executionResult, null, 2)}
        </Code>
      </Box>

      {/* Back Button */}
      <Box textAlign="center">
        <Button variant="link" colorScheme="blue" onClick={onClose}>
          ← Back to Saved Queries
        </Button>
      </Box>
    </Flex>
  );
};

export default QueryExecutor;
